import React, { useState, useEffect } from "react";
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import config from "../config.js";

import Layout from "../components/layout/GSTlayout";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TaskCalendar = () => {
  const [tasks, setTasks] = useState([]);
  const [, setLoading] = useState(false);

   const userId = useSelector((state) => state.auth.user?.id);
  const { API_BASE_URL } = config;

  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [newTask, setNewTask] = useState({
    title: "",
    date: new Date(),
    priority: "Medium",
    description: "",
    status: "Pending",
  });

  const fetchTasks = async () => {
    if (!userId) {
      showSnackbar("Missing user information", "error");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/getTasks?userId=${userId}`
      );

      if (
        response.data &&
        response.data.status === "success" &&
        Array.isArray(response.data.data)
      ) {
        setTasks(response.data.data);
        if (response.data.data.length === 0) {
          showSnackbar("No tasks found", "info");
        }
      } else {
        setTasks([]);
        console.warn("Unexpected API response structure:", response.data);
        showSnackbar("Error loading tasks data", "error");
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      showSnackbar("Error fetching tasks", "error");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const priorityData = {
    labels: ["Low", "Medium", "High"],
    datasets: [
      {
        label: "Task Priority Distribution",
        data: [
          tasks.filter((t) => t.priority === "Low").length,
          tasks.filter((t) => t.priority === "Medium").length,
          tasks.filter((t) => t.priority === "High").length,
        ],
        backgroundColor: [
          "rgba(76, 175, 80, 0.6)",
          "rgba(255, 152, 0, 0.6)",
          "rgba(244, 67, 54, 0.6)",
        ],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Task Priority Overview",
      },
    },
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewTask({
      title: "",
      date: new Date(),
      priority: "Medium",
      description: "",
      status: "Pending",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTask((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddTask = async () => {
    if (!newTask.title || !newTask.date) {
      showSnackbar("Please fill in all required fields", "error");
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/tasks`,
        newTask
      );

      if (response.data.success) {
        showSnackbar("Task added successfully");
        fetchTasks();
        handleCloseDialog();
      } else {
        throw new Error(response.data.error || "Failed to add task");
      }
    } catch (err) {
      showSnackbar(err.message, "error");
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "error";
      case "Medium":
        return "warning";
      default:
        return "success";
    }
  };

  return (
    <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h4" gutterBottom>
              Task Management Calendar
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Task Calendar
                    </Typography>
                    <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                      {tasks.map((task) => (
                        <Box
                          key={task.id}
                          sx={{
                            p: 2,
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: "background.paper",
                            boxShadow: 1,
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {task.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {task.description}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box
                                display="flex"
                                justifyContent="flex-end"
                                gap={1}
                              >
                                <Chip
                                  label={task.priority}
                                  color={getPriorityColor(task.priority)}
                                  size="small"
                                />
                                <Chip
                                  label={task.status}
                                  variant="outlined"
                                  size="small"
                                />
                                <Chip
                                  label={
                                    task.date
                                      ? new Date(task.date).toLocaleDateString()
                                      : "No Date Available"
                                  }
                                  variant="outlined"
                                  size="small"
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={5}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Task Priority Chart
                    </Typography>
                    <Bar data={priorityData} options={chartOptions} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenDialog(true)}
              sx={{ mt: 3 }}
            >
              Add New Task
            </Button>
          </CardContent>
        </Card>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add New Task</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Task Title"
                  name="title"
                  value={newTask.title}
                  onChange={handleInputChange}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={newTask.description}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Task Date"
                  type="date"
                  name="date"
                  value={newTask.date.toISOString().split("T")[0]}
                  onChange={(e) =>
                    setNewTask({
                      ...newTask,
                      date: new Date(e.target.value),
                    })
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    name="priority"
                    value={newTask.priority}
                    onChange={handleInputChange}
                    label="Priority"
                  >
                    <MenuItem value="Low">
                      <Chip label="Low" color="success" size="small" />
                    </MenuItem>
                    <MenuItem value="Medium">
                      <Chip label="Medium" color="warning" size="small" />
                    </MenuItem>
                    <MenuItem value="High">
                      <Chip label="High" color="error" size="small" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={newTask.status}
                    onChange={handleInputChange}
                    label="Status"
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleAddTask} variant="contained">
              Add Task
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default TaskCalendar;
